import { apiClient } from '@/lib/axios';

export type UiAuditStatus =
  | 'PASS'
  | 'FAIL'
  | 'NA'
  | 'PENDING'
  | 'AI_SUGGESTED_PASS'
  | 'AI_SUGGESTED_FAIL';
export type UiAuditSeverity = 'LOW' | 'MEDIUM' | 'HIGH';
export type UiAuditCheckMethod = 'AI_STATIC' | 'RUNTIME' | 'MANUAL';

export interface IUiAuditRule {
  id: string;
  code: string;
  section: string;
  description: string;
  defaultSeverity: UiAuditSeverity;
  checkMethod: UiAuditCheckMethod;
  orderNo: number;
  note: string | null;
  isDisabled: boolean;
  logUpdatedAt: string;
}

export interface IUiAuditScreen {
  id: string;
  code: string;
  name: string;
  route: string | null;
  orderNo: number;
}

export interface IUiAuditCell {
  ruleId: string;
  ruleCode: string;
  ruleSection: string;
  ruleDescription: string;
  ruleDefaultSeverity: UiAuditSeverity;
  ruleCheckMethod: UiAuditCheckMethod;
  ruleOrderNo: number;
  screenId: string;
  screenCode: string;
  screenName: string;
  cellId: string | null;
  status: UiAuditStatus;
  severity: UiAuditSeverity;
  notes: string | null;
  aiEvidence: string | null;
  aiModel: string | null;
  aiRunAt: string | null;
  aiConfidence: number | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  logUpdatedAt: string | null;
  logUpdatedBy: string | null;
}

export interface IUiAuditSummary {
  total: number;
  pass: number;
  fail: number;
  na: number;
  pending: number;
  aiSuggested: number;
  PASS: number;
  FAIL: number;
  NA: number;
  PENDING: number;
  AI_SUGGESTED_PASS: number;
  AI_SUGGESTED_FAIL: number;
}

export interface IUiAuditMatrix {
  rules: IUiAuditRule[];
  screens: IUiAuditScreen[];
  cells: IUiAuditCell[];
  summary: IUiAuditSummary;
  lastUpdated: { at: string; by: string | null } | null;
}

export interface IUiAuditAttachment {
  id: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  logCreatedAt: string;
  logCreatedBy: string | null;
}

export interface IUiAuditHistoryEntry {
  id: string;
  oldStatus: UiAuditStatus | null;
  newStatus: UiAuditStatus;
  oldSeverity: UiAuditSeverity | null;
  newSeverity: UiAuditSeverity | null;
  note: string | null;
  changedBy: string;
  changedAt: string;
}

export interface IUiAuditCellDetail {
  id: string | null;
  ruleId: string;
  screenId: string;
  status: UiAuditStatus;
  severity: UiAuditSeverity;
  notes: string | null;
  aiEvidence: string | null;
  aiModel: string | null;
  aiRunAt: string | null;
  aiConfidence: number | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  rule: IUiAuditRule;
  screen: IUiAuditScreen;
  attachments: IUiAuditAttachment[];
  history: IUiAuditHistoryEntry[];
}

export interface IUiAuditSnapshot {
  id: string;
  snapshotName: string;
  description: string | null;
  exportedAt: string;
  exportedBy: string;
  fileUrlExcel: string | null;
  fileUrlPdf: string | null;
  summaryJson: IUiAuditSummary & { filter?: unknown };
}

export interface IUiAuditFilter {
  section?: string;
  screenCode?: string;
  status?: UiAuditStatus;
  severity?: UiAuditSeverity;
  q?: string;
}

const unwrap = <T,>(res: { data: { success: boolean; data: T } | T }): T => {
  const d = res.data as { success?: boolean; data?: T };
  return (d && 'data' in d && d.data !== undefined ? d.data : (res.data as T)) as T;
};

export async function listUiAuditRules(): Promise<IUiAuditRule[]> {
  const res = await apiClient.get('/ui-audit/rules');
  return unwrap<IUiAuditRule[]>(res);
}

export async function createUiAuditRule(dto: {
  code?: string;
  section: string;
  description: string;
  defaultSeverity: UiAuditSeverity;
  checkMethod: UiAuditCheckMethod;
  note?: string;
}): Promise<IUiAuditRule> {
  const res = await apiClient.post('/ui-audit/rules', dto);
  return unwrap<IUiAuditRule>(res);
}

export async function updateUiAuditRule(
  id: string,
  dto: Partial<{
    section: string;
    description: string;
    defaultSeverity: UiAuditSeverity;
    checkMethod: UiAuditCheckMethod;
    note: string;
  }>,
): Promise<IUiAuditRule> {
  const res = await apiClient.patch(`/ui-audit/rules/${id}`, dto);
  return unwrap<IUiAuditRule>(res);
}

export async function deleteUiAuditRule(id: string): Promise<void> {
  await apiClient.delete(`/ui-audit/rules/${id}`);
}

export async function listUiAuditScreens(): Promise<IUiAuditScreen[]> {
  const res = await apiClient.get('/ui-audit/screens');
  return unwrap<IUiAuditScreen[]>(res);
}

export async function getUiAuditMatrix(filter: IUiAuditFilter = {}): Promise<IUiAuditMatrix> {
  const res = await apiClient.get('/ui-audit/cells', { params: filter });
  return unwrap<IUiAuditMatrix>(res);
}

export async function getUiAuditCell(ruleId: string, screenId: string): Promise<IUiAuditCellDetail> {
  const res = await apiClient.get(`/ui-audit/cells/${ruleId}/${screenId}`);
  return unwrap<IUiAuditCellDetail>(res);
}

export async function updateUiAuditCell(
  ruleId: string,
  screenId: string,
  dto: { status?: UiAuditStatus; severity?: UiAuditSeverity; notes?: string },
): Promise<IUiAuditCellDetail> {
  const res = await apiClient.patch(`/ui-audit/cells/${ruleId}/${screenId}`, dto);
  return unwrap<IUiAuditCellDetail>(res);
}


export async function addUiAuditAttachment(
  ruleId: string,
  screenId: string,
  dto: { fileUrl: string; fileName: string; fileSize: number; mimeType: string },
): Promise<IUiAuditAttachment> {
  const res = await apiClient.post(`/ui-audit/cells/${ruleId}/${screenId}/attachments`, dto);
  return unwrap<IUiAuditAttachment>(res);
}

export async function removeUiAuditAttachment(attachmentId: string): Promise<void> {
  await apiClient.delete(`/ui-audit/attachments/${attachmentId}`);
}

export interface IImportAiPayload {
  aiModel: string;
  aiRunAt?: string;
  cells: Array<{
    ruleCode: string;
    screenCode: string;
    status: 'Pass' | 'Fail' | 'NA';
    confidence?: number;
    evidence?: string;
    fileLine?: string;
  }>;
}

export interface IImportAiResult {
  imported: number;
  overridden: number;
  errors: string[];
  total: number;
}

export async function importUiAuditAi(payload: IImportAiPayload): Promise<IImportAiResult> {
  const res = await apiClient.post('/ui-audit/import-ai', payload);
  return unwrap<IImportAiResult>(res);
}

export async function downloadUiAuditExport(
  format: 'excel' | 'pdf',
  filter: IUiAuditFilter = {},
): Promise<void> {
  const res = await apiClient.get(`/ui-audit/export/${format}`, {
    params: filter,
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const ext = format === 'excel' ? 'xlsx' : 'pdf';
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, (m) => (m === 'T' ? '_' : ''));
  a.download = `UI-Audit_${stamp}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function listUiAuditSnapshots(): Promise<IUiAuditSnapshot[]> {
  const res = await apiClient.get('/ui-audit/snapshots');
  return unwrap<IUiAuditSnapshot[]>(res);
}

export async function createUiAuditSnapshot(
  dto: { snapshotName: string; description?: string },
  filter: IUiAuditFilter = {},
): Promise<IUiAuditSnapshot> {
  const res = await apiClient.post('/ui-audit/snapshots', dto, { params: filter });
  return unwrap<IUiAuditSnapshot>(res);
}

export async function downloadUiAuditSnapshot(id: string, format: 'excel' | 'pdf'): Promise<void> {
  const res = await apiClient.get(`/ui-audit/snapshots/${id}/download/${format}`, {
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `snapshot-${id}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
